'use client';

import Script from 'next/script';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { usePathname } from 'next/navigation';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { Button } from '@/_components';
import styles from './HubspotScript.module.css';
import { getClasses } from '@/_utils/classnames';
import { LayoutContext } from '@/_contexts/LayoutProvider';

export const HubspotScript = () => {
  const { user } = useContext(UserContext);
  const { firm } = useContext(FirmSettingsContext);
  const { isAssistantOpen } = useContext(LayoutContext);
  const pathname: string = usePathname();

  const shouldLoadHubspot: boolean = Boolean(
    user?.is_advisor &&
      !pathname.startsWith('/preview') &&
      firm?.account_type !== 'demo',
  );

  const [hasHubspotToggleClass, setHasHubspotToggleClass] =
    useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window._hsq) return;

    // If the user is not logged in but we have Hubspot loaded, we need to hard reset the identify cookie
    if (!user?.id) {
      console.debug('--> hubspot identify', {});
      window._hsq.push(['identify', {}]);
      return;
    }

    if (shouldLoadHubspot) {
      const identifyObject = {
        email: user.email,
        user_id: user.id,
        user_firm_access: user.firm_access,
        firm_id: firm?.id,
      };

      console.debug('--> hubspot identify', identifyObject);

      // Set up Hubspot tracking
      window._hsq.push(['setPath', pathname]);
      window._hsq.push(['trackPageView']);

      // Set the Hubspot identify record with logged in user details
      window._hsq.push(['identify', identifyObject]);
    }
  }, [
    pathname,
    shouldLoadHubspot,
    user?.id,
    user?.firm_access,
    user?.email,
    firm?.id,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    setHasHubspotToggleClass(document.body.classList.contains('hubspot-open'));
  }, []);

  // Observe HubSpot iframe size and toggle 'hubspot-expanded' on body
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const BODY_EXPANDED_CLASS = 'hubspot-expanded';

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let domObserver: MutationObserver | null = null;
    let targetEl: HTMLElement | null = null;

    const isExpanded = (): boolean => {
      if (!targetEl) return false;

      const rect = targetEl.getBoundingClientRect();

      return rect.width > 150 || rect.height > 150;
    };

    const updateBodyClass = () => {
      const expanded = isExpanded();
      document.body.classList.toggle(BODY_EXPANDED_CLASS, expanded);
    };

    const observeTarget = (el: HTMLElement) => {
      targetEl = el;
      resizeObserver = new ResizeObserver(() => updateBodyClass());
      resizeObserver.observe(el);
      mutationObserver = new MutationObserver(() => updateBodyClass());
      mutationObserver.observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });

      updateBodyClass();
    };

    const findTarget = (): HTMLElement | null => {
      const iframe = document.getElementById(
        'hubspot-conversations-iframe',
      ) as HTMLElement | null;

      if (iframe) return iframe;

      const container = document.getElementById(
        'hubspot-messages-iframe-container',
      ) as HTMLElement | null;

      if (container) return container;

      return null;
    };

    const initial = findTarget();

    if (initial) {
      observeTarget(initial);
    } else {
      domObserver = new MutationObserver(() => {
        const el = findTarget();

        if (el) {
          observeTarget(el);
          domObserver?.disconnect();
          domObserver = null;
        }
      });

      domObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      document.body.classList.remove(BODY_EXPANDED_CLASS);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      domObserver?.disconnect();
    };
  }, [shouldLoadHubspot]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const registerHandlers = () => {
      const api: any = (window as any).HubSpotConversations;
      if (!api || typeof api.on !== 'function') return;

      const handleOpened = () => {
        document.body.classList.add('hubspot-open');
        setHasHubspotToggleClass(true);
      };

      const handleClosed = () => {
        document.body.classList.remove('hubspot-open');
        setHasHubspotToggleClass(false);
      };

      api.on('widgetOpened', handleOpened);
      api.on('widgetClosed', handleClosed);
    };

    if ((window as any).HubSpotConversations?.widget) {
      registerHandlers();
    } else {
      (window as any).hsConversationsOnReady =
        (window as any).hsConversationsOnReady || [];
      (window as any).hsConversationsOnReady.push(registerHandlers);
    }
  }, [shouldLoadHubspot]);

  const toggleHubspotClass = () => {
    if (typeof document === 'undefined') return;

    const next = !document.body.classList.contains('hubspot-open');

    document.body.classList.toggle('hubspot-open', next);
    setHasHubspotToggleClass(next);

    if (typeof window === 'undefined') return;

    const enqueue = (fn: () => void) => {
      (window as any).hsConversationsOnReady =
        (window as any).hsConversationsOnReady || [];
      (window as any).hsConversationsOnReady.push(fn);
    };

    if (next) {
      const openFn = () => window.HubSpotConversations?.widget?.open?.();

      if ((window as any).HubSpotConversations?.widget?.open) openFn();
      else enqueue(openFn);
    } else {
      const widgetAny = (window as any).HubSpotConversations?.widget;
      const closeFn = () => widgetAny?.close && widgetAny.close();

      if (widgetAny?.close) closeFn();
      else enqueue(closeFn);
    }
  };

  const hubspotButtonClasses: string = getClasses(styles.hubspotScriptButton, {
    [styles.hubspotScriptButtonOpen]: hasHubspotToggleClass,
  });

  if (!shouldLoadHubspot || isAssistantOpen) return null;

  return (
    <>
      <Button
        className={hubspotButtonClasses}
        value="Toggle HubSpot"
        isIconOnly={true}
        iconLeft="chat"
        data-testid="hubspot-toggle-button"
        isRounded={true}
        onClick={toggleHubspotClass}
        intent="primary"
      />
      <Script
        id="hs-script-loader"
        src="//js-na1.hs-scripts.com/44838654.js"
        strategy="lazyOnload"
        onLoad={() => {
          window._hsq = window._hsq || [];
        }}
      />
    </>
  );
};
